import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Check,
  Loader2,
  Star,
  Clock,
  CreditCard,
  AlertTriangle,
  TrendingUp,
  Zap,
} from 'lucide-react';
import type { PluginManifest } from '@/types';
import type { PluginPlanTier } from '@/lib/api';
import { getCurrencySymbol } from '@/lib/money';

interface UsageData {
  plan: string;
  period: string;
  current: { conversations: number; aiMessages: number; messages: number };
  limits: { conversations: number; aiMessages: number };
  allowed: boolean;
  upsell: {
    reason: string;
    percentUsed: number;
    message: string;
    severity: 'warning' | 'critical';
  } | null;
}

interface PluginBillingProps {
  manifest: PluginManifest;
  projectId: string;
  pluginId: string;
  usage?: UsageData | null;
  /** Product kind for gateway checkout — 'addon' for platform add-ons (Speed
   *  Booster), 'plugin' (default) for installed plugins. */
  productType?: 'plugin' | 'addon';
}

/**
 * Billing tab — reseller flow for aapastech-owned plugins.
 * Shows plan cards, current usage bars, subscription management, and
 * smart upgrade suggestions when usage approaches limits.
 */
export function PluginBilling({
  manifest,
  projectId,
  pluginId,
  usage,
  productType = 'plugin',
}: PluginBillingProps) {
  const queryClient = useQueryClient();

  // Gateway-first checkout for PAID tiers (self-serve never uses credits).
  const checkoutMutation = useMutation({
    mutationFn: (tier: string) =>
      apiHelpers.checkoutProduct(productType, pluginId, tier, projectId),
    onSuccess: (response) => {
      const url = (response as any)?.data?.data?.checkoutUrl;
      if (url) {
        window.location.href = url; // redirect to Razorpay/Stripe
      } else {
        toast.success('Subscription activated');
        queryClient.invalidateQueries({ queryKey: ['plugin-subscription', projectId, pluginId] });
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Could not start checkout');
    },
  });

  // Custom confirmation dialog (replaces native window.confirm for a branded UX).
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    danger?: boolean;
    action: () => void;
  } | null>(null);

  const isTyashinBilled = manifest.pricing?.billingManagedBy === 'tyashin';

  const plansQuery = useQuery({
    queryKey: ['plugin-plans', projectId, pluginId],
    queryFn: () => apiHelpers.getPluginPlans(projectId, pluginId),
    enabled: isTyashinBilled,
  });

  const subscriptionQuery = useQuery({
    queryKey: ['plugin-subscription', projectId, pluginId],
    queryFn: () => apiHelpers.getPluginSubscription(projectId, pluginId),
    enabled: isTyashinBilled,
  });

  const subscribeMutation = useMutation({
    mutationFn: (tier: string) =>
      apiHelpers.subscribeToPluginTier(projectId, pluginId, tier, 'credits'),
    onSuccess: (response) => {
      const res = (response as any)?.data;
      if (res?.success) {
        toast.success('Subscription activated');
        queryClient.invalidateQueries({ queryKey: ['plugin-subscription', projectId, pluginId] });
        queryClient.invalidateQueries({ queryKey: ['plugin-manifest', projectId, pluginId] });
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Subscription failed');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiHelpers.cancelPluginSubscription(projectId, pluginId),
    onSuccess: (response) => {
      const effectiveAt = (response as any)?.data?.data?.effectiveAt;
      toast.success(
        effectiveAt
          ? `Subscription will end on ${new Date(effectiveAt).toLocaleDateString()}`
          : 'Subscription cancelled'
      );
      queryClient.invalidateQueries({ queryKey: ['plugin-subscription', projectId, pluginId] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Cancel failed');
    },
  });

  // Vendor-billed plugins
  if (!isTyashinBilled) {
    return (
      <div className="bg-card rounded-xl border p-8 text-center">
        <CreditCard className="text-muted-foreground mx-auto mb-3 h-10 w-10" />
        <h3 className="font-semibold">Billed by {manifest.name}</h3>
        <p className="text-muted-foreground mx-auto mt-1 max-w-md text-sm">
          {manifest.name} handles its own billing. Manage your subscription directly with the
          vendor.
        </p>
        {manifest.links?.website && (
          <a
            href={manifest.links.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary mt-3 inline-block text-sm hover:underline"
          >
            Visit vendor site &rarr;
          </a>
        )}
      </div>
    );
  }

  if (plansQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }

  const plansData = plansQuery.data?.data?.data;
  const subscription = subscriptionQuery.data?.data?.data;
  const tiers = plansData?.tiers || [];
  const currency = plansData?.currency || 'USD';
  const currentTier = subscription?.plan || usage?.plan || 'free';
  const recurringValue = plansData?.recurringValue as
    | { headline: string; points: string[] }
    | null
    | undefined;

  if (!plansData?.hasPaidTiers) {
    return (
      <div className="bg-card rounded-xl border p-8 text-center">
        <p className="text-muted-foreground">This plugin has no paid tiers.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Upsell banner (when usage approaches limits) ── */}
      {usage?.upsell && (
        <div
          className={`flex items-start gap-3 rounded-xl border p-5 ${
            usage.upsell.severity === 'critical'
              ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/10'
              : 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10'
          }`}
        >
          <div className="mt-0.5">
            {usage.upsell.severity === 'critical' ? (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            ) : (
              <TrendingUp className="h-5 w-5 text-amber-500" />
            )}
          </div>
          <div className="flex-1">
            <p className="font-medium">
              {usage.upsell.severity === 'critical'
                ? 'You are about to hit your plan limit'
                : 'Your usage is growing'}
            </p>
            <p className="text-muted-foreground mt-0.5 text-sm">{usage.upsell.message}</p>
          </div>
          <Zap className="h-5 w-5 text-amber-500" />
        </div>
      )}

      {/* ── Usage meters (when data available) ────────── */}
      {usage && (
        <div className="bg-card rounded-xl border p-5">
          <h4 className="mb-4 text-sm font-medium">
            Current usage
            <span className="text-muted-foreground ml-2 font-normal">({usage.period})</span>
          </h4>
          <div className="grid gap-4 md:grid-cols-2">
            <UsageBar
              label="Conversations"
              current={usage.current.conversations}
              limit={usage.limits.conversations}
            />
            <UsageBar
              label="AI Messages"
              current={usage.current.aiMessages}
              limit={usage.limits.aiMessages}
            />
          </div>
          <p className="text-muted-foreground mt-3 text-xs">
            {usage.current.messages} total messages exchanged this period
          </p>
        </div>
      )}

      {/* ── Current subscription banner ────────────────── */}
      {subscription && subscription.plan !== 'free' && (
        <div
          className={`rounded-xl border p-5 ${
            subscription.cancelAtPeriodEnd
              ? 'bg-yellow-50 dark:bg-yellow-900/10'
              : 'bg-green-50 dark:bg-green-900/10'
          }`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-medium">
                {subscription.cancelAtPeriodEnd
                  ? 'Subscription scheduled to end'
                  : 'Active subscription'}
              </p>
              <p className="text-muted-foreground mt-0.5 text-sm">
                {subscription.cancelAtPeriodEnd
                  ? `You'll remain on the ${subscription.plan} plan until ${new Date(
                      subscription.currentPeriodEnd
                    ).toLocaleDateString()}, then revert to free.`
                  : `${subscription.plan} plan \u00b7 ${formatPrice(subscription.price, subscription.currency)}/mo \u00b7 renews ${new Date(
                      subscription.currentPeriodEnd
                    ).toLocaleDateString()}`}
              </p>
            </div>
            {!subscription.cancelAtPeriodEnd && subscription.plan !== 'free' && (
              <button
                onClick={() =>
                  setConfirm({
                    title: 'Cancel subscription?',
                    message:
                      'Your subscription will stop renewing. You keep access until the end of the current billing period, then revert to free.',
                    confirmLabel: 'Cancel subscription',
                    danger: true,
                    action: () => cancelMutation.mutate(),
                  })
                }
                disabled={cancelMutation.isPending}
                className="text-sm text-red-600 hover:underline disabled:opacity-50"
              >
                {cancelMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Why subscription? The recurring-value story ─── */}
      {recurringValue && recurringValue.points?.length > 0 && (
        <div className="border-primary/30 from-primary/5 mb-6 rounded-xl border bg-gradient-to-br to-transparent p-5">
          <div className="mb-3 flex items-center gap-2">
            <TrendingUp className="text-primary h-5 w-5" />
            <h3 className="font-semibold">{recurringValue.headline}</h3>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2">
            {recurringValue.points.map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <Zap className="text-primary mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span className="text-muted-foreground">{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Plan cards ────────────────────────────────── */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {tiers.map((tier: PluginPlanTier) => {
          const isCurrent = tier.tier === currentTier;
          const isFree = tier.priceMajor === 0;
          const isUpgrade =
            !isFree && (currentTier === 'free' || tierRank(tier.tier) > tierRank(currentTier));
          const isDowngrade = !isFree && !isCurrent && tierRank(tier.tier) < tierRank(currentTier);
          // Never recommend a tier at or below what the customer already has —
          // a "Recommended" star on a downgrade reads as a billing mistake.
          const showRecommended = Boolean(
            tier.recommended && !isCurrent && tierRank(tier.tier) > tierRank(currentTier)
          );

          return (
            <div
              key={tier.tier}
              className={`relative flex flex-col rounded-xl border p-5 transition-colors ${
                showRecommended ? 'border-primary ring-primary/20 ring-2' : ''
              } ${isCurrent ? 'bg-muted/30' : 'bg-card'}`}
            >
              {showRecommended && (
                <div className="bg-primary text-primary-foreground absolute -top-3 left-4 flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium">
                  <Star className="h-3 w-3" />
                  Recommended
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 left-4 flex items-center gap-1 rounded-full bg-green-500 px-2 py-0.5 text-xs font-medium text-white">
                  <Check className="h-3 w-3" />
                  Current
                </div>
              )}

              <h4 className="mt-1 font-semibold">{tier.displayName}</h4>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-2xl font-bold">
                  {isFree ? 'Free' : formatPrice(tier.priceMajor, tier.currency)}
                </span>
                {!isFree && (
                  <span className="text-muted-foreground text-sm">
                    /{tier.interval === 'year' ? 'yr' : 'mo'}
                  </span>
                )}
              </div>

              <ul className="text-muted-foreground mt-4 flex-1 space-y-1.5 text-sm">
                {tier.features.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-500" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => {
                  if (isCurrent) return;
                  const downgrade = isFree || isDowngrade;
                  const per = tier.interval === 'year' ? 'yr' : 'mo';
                  setConfirm({
                    title: isFree
                      ? 'Switch to the Free plan?'
                      : `${downgrade ? 'Downgrade to' : 'Subscribe to'} ${tier.displayName}?`,
                    message: isFree
                      ? 'Your paid features will remain available until the end of the current billing period, then this project moves to the Free plan.'
                      : `You'll be taken to secure payment to subscribe to ${tier.displayName} for ${formatPrice(tier.priceMajor, tier.currency)}/${per}. It auto-renews each ${per === 'yr' ? 'year' : 'month'}; cancel anytime.`,
                    confirmLabel: isFree
                      ? 'Switch to Free'
                      : downgrade
                        ? 'Continue to payment'
                        : 'Continue to payment',
                    danger: downgrade,
                    action: isFree
                      ? () => subscribeMutation.mutate(tier.tier)
                      : () => checkoutMutation.mutate(tier.tier),
                  });
                }}
                disabled={isCurrent || subscribeMutation.isPending || checkoutMutation.isPending}
                className={`mt-5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isCurrent
                    ? 'bg-muted text-muted-foreground cursor-default'
                    : isUpgrade && tier.recommended
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : isUpgrade
                        ? 'bg-primary/80 text-primary-foreground hover:bg-primary/90'
                        : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {(subscribeMutation.isPending && subscribeMutation.variables === tier.tier) ||
                (checkoutMutation.isPending && checkoutMutation.variables === tier.tier) ? (
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                ) : isCurrent ? (
                  'Current plan'
                ) : isUpgrade ? (
                  <>
                    <Zap className="mr-1 inline h-3.5 w-3.5" />
                    Upgrade
                  </>
                ) : isFree || isDowngrade ? (
                  'Downgrade'
                ) : (
                  'Subscribe'
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Fine print ────────────────────────────────── */}
      <div className="text-muted-foreground flex items-start gap-2 text-xs">
        <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <p>
          Billed monthly to your Tyashin account in {currency}. Payment currently via team credits.
          Subscriptions auto-renew; cancel anytime to stop renewal at the end of the current period.
        </p>
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title ?? ''}
        message={confirm?.message ?? ''}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onConfirm={() => {
          const act = confirm?.action;
          setConfirm(null);
          act?.();
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function UsageBar({ label, current, limit }: { label: string; current: number; limit: number }) {
  const unlimited = limit === 0;
  const percent = unlimited ? 0 : Math.min(100, (current / limit) * 100);
  const barColor = percent >= 95 ? 'bg-red-500' : percent >= 80 ? 'bg-amber-500' : 'bg-green-500';

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground text-xs">
          {current.toLocaleString()} {unlimited ? '(unlimited)' : `/ ${limit.toLocaleString()}`}
        </span>
      </div>
      {!unlimited && (
        <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
      {!unlimited && percent >= 80 && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          {percent >= 95
            ? 'Almost at limit \u2014 upgrade to avoid service interruption'
            : `${Math.round(percent)}% used \u2014 consider upgrading`}
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format a MAJOR-unit amount. Plugin tier prices (`tier.priceMajor` from
 * /plans) and the persisted addon `subscription.price` are catalogued in major
 * units (1 credit = 1 major unit) \u2014 the ONE deliberate exception to the
 * minor-units-everywhere contract in @/lib/money. Do NOT route these through
 * formatMoney (it divides by 100).
 */
function formatPrice(amountMajor: number, currency: string): string {
  const symbol = getCurrencySymbol(currency);
  // Defensive: if the wire ever sends a non-numeric price (e.g. a backend that
  // predates the priceMajor rename shipping alongside a newer SPA), degrade to
  // a placeholder instead of throwing on undefined.toLocaleString() — a single
  // bad tier must not white-screen the whole Plans tab.
  if (typeof amountMajor !== 'number' || !Number.isFinite(amountMajor)) {
    return `${symbol}—`;
  }
  return `${symbol}${amountMajor.toLocaleString('en-US')}`;
}

/** Rank tiers for upgrade/downgrade comparison */
function tierRank(tier: string): number {
  const ranks: Record<string, number> = {
    free: 0,
    starter: 1,
    pro: 2,
    business: 3,
    enterprise: 4,
  };
  return ranks[tier] ?? 0;
}
